import React, {useState, useEffect, useMemo} from 'react';
import {useIsFocused, useNavigation, useRoute} from '@react-navigation/native';
import {
  Alert as RNAlert,
  Linking,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
} from 'react-native';
import {useTheme} from 'styled-components/native';
import {cpf as cpfValidator} from 'cpf-cnpj-validator';

import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {Header} from '../../../components/Interface/Headers/Header';
import {Titles} from '../../../components/Interface/Typographies/Titles';
import {Picker} from '../../../components/Interface/Forms/Inputs/Picker';
import {Loading} from '../../../components/Interface/Loadings/Loading';

import {useAuth} from '../../../context/auth';

import {
  Container,
  Content,
  InputGroup,
  InputGrid,
  Form,
  FlagContainer,
  Flag,
  Footer,
  LinkButtonContainer,
  LinkButton,
  LinkButtonText,
  Step,
  CircleStep,
  LineStep,
  LineStepSeparator,
  StepText,
  List,
} from './styles';

import {api} from '../../../services/api';
import {UserCreditCardsDTO} from '../../../dtos/UserCreditCardsDTO';
import {Input} from '../../../components/Interface/Forms/Inputs/Input';
import AsyncStorage from '@react-native-async-storage/async-storage';

import {estados} from '../../../utils/estados';
import useCep from '../../../context/cep';
import {StepList} from '../../../components/Interface/List/StepList';
import {SafeAreaView} from 'react-native-safe-area-context';
import {RFValue} from 'react-native-responsive-fontsize';
import { useCards } from '../../../context/cards';

type Params = {
  voucher: boolean;
};

export function AddCreditCard() {
  const navigation = useNavigation();
  const theme = useTheme();
  const isFocused = useIsFocused();
  const {cardBrandImage, fetchUserCreditCardList} = useCards();

  const route = useRoute();

  const {voucher} = route.params as Params;

  const {user} = useAuth();
  const {buscaCep} = useCep();

  // CARTÃO
  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [brand, setBrand] = useState('select');
  const [brandImage, setBrandImage] = useState('');

  // ENDEREÇO
  const [cep, setCep] = useState('');
  const [endereco, setEndereco] = useState('');
  const [complemento, setComplemento] = useState('');
  const [numero, setNumero] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('select');

  const [cpfError, setCpfError] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const formCartaoHabilitado = useMemo(
    () =>
      number.length >= 14 &&
      expiry.length >= 5 &&
      cpf.length === 11 &&
      nome.length >= 4 &&
      brand !== 'select',
    [number, expiry, cpf, nome, brand],
  );

  const formEnderecoHabilitado = useMemo(
    () =>
      cep.length === 8 &&
      endereco.length > 4 &&
      cidade.length > 2 &&
      !!estado &&
      estado !== 'select' &&
      !!numero,
    [cep, endereco, cidade, estado, numero],
  );

  const [step, setStep] = useState<'card' | 'address'>('card');

  async function createCredCard() {
    const newExpiry = expiry.split('/');

    const month = newExpiry[0];
    const year = newExpiry[1];

    const formattedExpiry = month + '/' + '20' + year;

    await api
      .post('/cred_card/create', {
        personId: user.user_id,
        number,
        expiry: formattedExpiry,
        brand,
        cpf,
        voucher,
        endereco,
        numero,
        cidade,
        estado,
        complemento,
        cep,
        pais: 'BR',
        nome,
      })
      .then(() => {
        fetchUserCreditCardList({cardNumber: number});
        navigation.goBack();
      })
      .catch(({response}) => {
        if (response.status === 406) {
          return navigation.navigate('ConfirmationScreen', {
            type: 'error',
            closeIconAction: () => navigation.goBack(),
            title: 'Cartão não cadastrado',
            text: 'Não foi possível cadastrar o seu cartão. Esse cartão pertence a outra pessoa.',
            buttonText: 'Tentar novamente',
            buttonAction: () => navigation.goBack(),
          });
        }

        navigation.navigate('ConfirmationScreen', {
          type: 'error',
          closeIconAction: () => navigation.goBack(),
          title: 'Cartão não cadastrado',
          text: 'Não foi possível cadastrar o seu cartão. Ocorreu um problema no servidor interno.',
          buttonText: 'Tentar novamente',
          buttonAction: () => navigation.goBack(),
        });
      });
  }

  async function addCreditCard() {
    let isMounted = true;
    setIsLoading(true);

    try {
      await api.get('/cred_card').then(async res => {
        const activeCards = res.data.find(
          (card: UserCreditCardsDTO) => card.number === number,
        );

        if (activeCards) {
          navigation.navigate('ConfirmationScreen', {
            type: 'error',
            closeIconAction: () => navigation.goBack(),
            title: 'Cartão não cadastrado',
            text: 'Não foi possível cadastrar o seu cartão. Esse cartão já está em sua carteira.',
            buttonText: 'Voltar',
            buttonAction: () => navigation.goBack(),
          });
        } else {
          createCredCard();
        }
      });
    } catch {
      RNAlert.alert(
        'Serviço indisponível',
        'Verifique sua conexão com a internet',
      );
    } finally {
      if (isMounted) {
        setIsLoading(false);
        isMounted = false;
      }
    }

    return () => {
      isMounted = false;
    };
  }

  const handleGoBack = () => {
    if (step === 'card') {
      return navigation.goBack();
    }
    return setStep('card');
  };

  const handleChangeCep = async (_: string, extracted = '') => {
    setCep(extracted);
    if (extracted.length === 8) {
      try {
        const {data} = await buscaCep(extracted);
        if (data?.erro) {return;}
        setCidade(data.localidade);
        setComplemento(data.complemento);
        setEndereco(data.logradouro);
        setEstado(data.uf);
      } catch (error) {}
    }
  };

  const setNextStep = () => {
    const verifyCpfIsValid = cpfValidator.isValid(cpf);

    if (!verifyCpfIsValid) {
      return setCpfError(true);
    }

    setStep('address');
  };

  useEffect(() => {
    setBrandImage(cardBrandImage(brand));
  }, [brand]);

  useEffect(() => {
    let isMounted = true;

    async function verifyTermShowed() {
      const term = await AsyncStorage.getItem('creditcard_term');
      if (term === null) {
        if (isMounted) {
          setIsLoading(false);
          navigation.navigate('AcceptCreditCardRegulament');
          isMounted = false;
        }
      }
    }

    verifyTermShowed();

    return () => {
      isMounted = false;
    };
  }, [isFocused]);

  return (
    <>
      {isLoading && <Loading />}
      <SafeAreaView edges={['bottom']} style={{flex: 1}}>
        <Header icon="chevron-left" action={handleGoBack} />

        <KeyboardAvoidingView
          style={{flex: 1}}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              paddingBottom: Platform.OS === 'ios' ? RFValue(20) : RFValue(40),
            }}>
            <Container>
              <Content>
                {step === 'card' && (
                  <Titles
                    title={voucher ? 'Adicionar vale' : 'Adicionar cartão'}
                    subtitle={
                      voucher
                        ? 'Digite os dados do vale abaixo:'
                        : 'Digite os dados do cartão abaixo:'
                    }
                  />
                )}

                {step === 'address' && (
                  <Titles
                    title="Endereço da fatura"
                    subtitle="Digite o endereço da sua fatura:"
                  />
                )}

                <Step>
                  <LineStepSeparator active width="50px" />
                  <CircleStep status={step === 'card' ? 'editing' : 'done'}>
                    <StepText active={true} width={55}>
                      Cartão
                    </StepText>
                  </CircleStep>
                  <LineStep active={step === 'address'} />
                  <CircleStep
                    status={step === 'address' ? 'editing' : 'pending'}>
                    <StepText active={step === 'address'} width={70}>
                      Endereço
                    </StepText>
                  </CircleStep>
                  <LineStepSeparator active={false} width="50px" />
                </Step>

                {step === 'card' && (
                  <Form>
                    <InputGroup>
                      <Input
                        labelText="Número do cartão"
                        placeholder="9999 9999 9999 9999"
                        value={number}
                        keyboardType="numeric"
                        onChangeText={(_, extracted: any) =>
                          setNumber(extracted)
                        }
                        mask={'[0000] [0000] [0000] [0000]'}
                      />
                    </InputGroup>
                    <InputGrid>
                      <InputGroup style={{flex: 1}}>
                        <Input
                          labelText="Vencimento"
                          placeholder="10/28"
                          value={expiry}
                          keyboardType="numeric"
                          onChangeText={(formatted: any) => {
                            const month = parseInt(formatted.substring(0, 2), 10);
                            if (formatted.length >= 2 && (month < 1 || month > 12)) {
                              return;
                            }
                            setExpiry(formatted);
                          }}
                          mask={'[00]/[00]'}
                        />
                      </InputGroup>
                      <InputGroup style={{flex: 1}}>
                        <Picker
                          label={'Bandeira '}
                          value={brand}
                          placeholder={{
                            label: 'Selecione',
                            value: 'select',
                          }}
                          onChangeValue={value => setBrand(value)}
                          leftComponent={
                            <FlagContainer>
                              <Flag
                                source={{
                                  uri: brandImage,
                                }}
                                resizeMode="stretch"
                              />
                            </FlagContainer>
                          }
                          items={
                            voucher
                              ? [
                                  {label: 'VR (Auto ou Multi)', value: 'vr'},
                                  {
                                    label:
                                      'Pluxee (Combustível, Multi ou Gift)',
                                    value: 'pluxee',
                                  },
                                ]
                              : [
                                  {label: 'Mastercard', value: 'Master'},
                                  {label: 'Elo', value: 'Elo'},
                                  {label: 'American Express', value: 'Amex'},
                                  {label: 'Visa', value: 'Visa'},
                                  {label: 'Hipercard', value: 'Hiper'},
                                ]
                          }
                        />
                      </InputGroup>
                    </InputGrid>
                    <InputGroup>
                      <Input
                        labelText="CPF do titular do cartão"
                        placeholder="000.000.000-00"
                        value={cpf}
                        keyboardType="number-pad"
                        onChangeText={(_, extracted: any) => {
                          return setCpf(extracted);
                        }}
                        mask={'[000].[000].[000]-[00]'}
                        style={{
                          color: cpfError
                            ? theme.colors.attention
                            : theme.colors.title,
                          borderColor: cpfError
                            ? theme.colors.attention
                            : theme.colors.border,
                        }}
                      />
                    </InputGroup>
                    <InputGroup>
                      <Input
                        labelText="Nome no cartão"
                        value={nome}
                        onChangeText={setNome}
                        placeholder="Digite o nome no cartão"
                      />
                    </InputGroup>

                    {brand === 'vr' && (
                      <List>
                        <StepList
                          containerStyle={{
                            backgroundColor: theme.colors.orange_opacity,
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderColor: theme.colors.orange,
                          }}
                          textStyle={{
                            color: theme.colors.orange_dark,
                            fontFamily: theme.fonts.medium,
                          }}
                          text="Só são aceitos os tipos VR Auto ou VR Multi. Outros cartões VR (como Alimentação ou Refeição) não são compatíveis."
                        />
                      </List>
                    )}
                    {brand === 'pluxee' && (
                      <List>
                        <StepList
                          containerStyle={{
                            backgroundColor: theme.colors.orange_opacity,
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderColor: theme.colors.orange,
                          }}
                          textStyle={{
                            color: theme.colors.orange_dark,
                            fontFamily: theme.fonts.medium,
                          }}
                          text="Só são aceitos os tipos Combustível, Multibenefícios ou Gift. Outros cartões da bandeira, como Alimentação ou Refeição, não são compatíveis."
                        />
                      </List>
                    )}
                  </Form>
                )}

                {step === 'address' && (
                  <Form>
                    <InputGrid>
                      <InputGroup style={{flex: 1}}>
                        <Input
                          labelText="Cep"
                          mask="[00000]-[000]"
                          value={cep}
                          keyboardType="number-pad"
                          onChangeText={(_, extracted) =>
                            handleChangeCep(_, extracted)
                          }
                        />
                      </InputGroup>
                      <InputGroup style={{flex: 1}}>
                        <Picker
                          label={'Estado'}
                          value={estado}
                          placeholder={{
                            label: 'Selecione',
                            value: 'select',
                          }}
                          onChangeValue={setEstado}
                          items={estados}
                        />
                      </InputGroup>
                    </InputGrid>
                    <InputGroup style={{flex: 1}}>
                      <Input
                        labelText="Endereço"
                        value={endereco}
                        onChangeText={setEndereco}
                      />
                    </InputGroup>
                    <InputGrid>
                      <InputGroup style={{flex: 1}}>
                        <Input
                          labelText="Número"
                          value={numero}
                          keyboardType="number-pad"
                          onChangeText={setNumero}
                          maxLength={9}
                        />
                      </InputGroup>
                      <InputGroup style={{flex: 1}}>
                        <Input
                          labelText="Complemento"
                          value={complemento}
                          onChangeText={setComplemento}
                        />
                      </InputGroup>
                    </InputGrid>
                    <InputGrid>
                      <InputGroup style={{flex: 1}}>
                        <Input
                          labelText="Cidade"
                          value={cidade}
                          onChangeText={setCidade}
                        />
                      </InputGroup>
                    </InputGrid>

                    <LinkButtonContainer>

                        <LinkButton
                          activeOpacity={1}
                          onPress={() =>
                            Linking.openURL(
                              'https://barataocombustiveis.com.br/termos-e-condicoes',
                            )
                          }>
                          <LinkButtonText>Termos de uso</LinkButtonText>
                        </LinkButton>

                    </LinkButtonContainer>
                  </Form>
                )}
              </Content>

              <Footer>
                {step === 'card' && (
                  <Button
                    title={'Continuar'}
                    onPress={setNextStep}
                    enabled={formCartaoHabilitado}
                  />
                )}
                {step === 'address' && (
                  <Button
                    title={'Adicionar cartão'}
                    onPress={() => addCreditCard()}
                    enabled={formEnderecoHabilitado}
                  />
                )}
              </Footer>
            </Container>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
}
